import { Card, FormHelperText, Grid, TextField } from '@mui/material';
import React, { ChangeEvent, useState } from 'react';
import AppHeader from '../../common/app-header/AppHeader';
import { CustomerWithId } from '../../models/customer.model';
import Button from '@mui/material/Button';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../common/constants/routes';
import { useAppSelector } from '../../common/hooks/useAppSelector';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';
import { createCustomer } from '../../slices/customers/customersSlice';
import { useAppDispatch } from '../../common/hooks/useAppDispatch';
import { useEmailField } from '../../common/hooks/useEmailField';
import { useTranslation } from 'react-i18next';
import { useErrorMessage } from '../../common/hooks/useErrorMessage';

export const CreateCustomerProfile: React.FC = () => {
  const company = useAppSelector(selectAllCompanies)[0];
  const [formData, setFormData] = useState<Partial<CustomerWithId>>({
    company: company.id,
    customer_name: '',
    contact_name: '',
    contact_number: '',
    email_address: '',
    physical_address: '',
    postal_address: '',
    industy_type: '',
    website: '',
    customer_registration: '',
  });
  const [fieldsErrors, setFieldsErrors] = useState<Record<string, string[]> | null>(null);
  const [pendingStatus, setPendingStatus] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { getErrorMessage } = useErrorMessage();

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Lower cases what is typed and applies the shared basic email validation.
  const emailField = useEmailField({
    value: formData.email_address,
    onChange: (value: string) => setFormData((prev) => ({ ...prev, email_address: value })),
  });

  const handleSubmit = (e: React.SyntheticEvent) => {
    e.preventDefault();
    const errors: Record<string, string[]> = {};
    if (!formData.customer_name) {
      errors.customer_name = ['Customer name is required'];
    }
    // Optional field, but it has to be a valid address when filled in.
    // The message is rendered by the field itself, so it is not added to `errors`.
    const isEmailValid = emailField.validate();

    if (Object.keys(errors).length > 0 || !isEmailValid) {
      setFieldsErrors(Object.keys(errors).length > 0 ? errors : null);
      return;
    }
    setPendingStatus(true);
    dispatch(createCustomer(formData))
      .unwrap()
      .then((res) => {
        const createdCustomer = res as CustomerWithId;
        toast.success('Customer profile created successfully!');
        setFormData({
          company: company.id,
          customer_name: '',
          contact_name: '',
          contact_number: '',
          email_address: '',
          physical_address: '',
          postal_address: '',
          industy_type: '',
          website: '',
        });
        setFieldsErrors(null);
        navigate(ROUTES.CUSTOMER_PROFILES + '/' + createdCustomer.id);
      })
      .catch((error) => {
        setFieldsErrors(error.response.data);
        toast.error(getErrorMessage(error));
      })
      .finally(() => {
        setPendingStatus(false);
      });
  };

  return (
    <>
      <AppHeader title={t('pages.createCustomer')} />
      <div className='profile-details-wrapper'>
        <form onSubmit={handleSubmit}>
          <Card sx={{ padding: '16px', marginBottom: '1rem' }}>
            <Grid component='div' container className='form-controls' spacing={2}>
              <Grid component='div' size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={`${t('labels.customerName')} *`}
                  onChange={handleInputChange}
                  name='customer_name'
                  value={formData.customer_name}
                  error={Boolean(fieldsErrors?.customer_name)} // Add this line
                  helperText={fieldsErrors?.customer_name?.join('.')}
                />
              </Grid>
              <Grid component='div' size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={`${t('labels.contactName')}`}
                  onChange={handleInputChange}
                  name='contact_name'
                  value={formData.contact_name}
                />
              </Grid>
              <Grid component='div' size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={`${t('labels.contactNumber')}`}
                  onChange={handleInputChange}
                  name='contact_number'
                  value={formData.contact_number}
                />
                {Boolean(fieldsErrors?.contact_number) && (
                  <FormHelperText error={true}>
                    {fieldsErrors?.contact_number.join('.')}
                  </FormHelperText>
                )}
              </Grid>
              <Grid component='div' size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={`${t('labels.emailAddress')}`}
                  name='email_address'
                  {...emailField.fieldProps}
                  error={emailField.hasError || Boolean(fieldsErrors?.email_address)}
                  helperText={
                    emailField.errorMessage || fieldsErrors?.email_address?.join('.') || undefined
                  }
                />
              </Grid>
              <Grid component='div' size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={`${t('labels.customerRegistrationNumber')}`}
                  onChange={handleInputChange}
                  name='customer_registration'
                  value={formData.customer_registration}
                />
              </Grid>
              <Grid component='div' size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={`${t('labels.physicalAddress')}`}
                  onChange={handleInputChange}
                  name='physical_address'
                  value={formData.physical_address}
                />
              </Grid>
              <Grid component='div' size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={`${t('labels.postalAddress')}`}
                  onChange={handleInputChange}
                  name='postal_address'
                  value={formData.postal_address}
                />
              </Grid>
              <Grid component='div' size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={`${t('labels.industryType')}`}
                  onChange={handleInputChange}
                  name='industy_type'
                  value={formData.industy_type}
                />
              </Grid>
              <Grid component='div' size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label={`${t('labels.website')}`}
                  onChange={handleInputChange}
                  name='website'
                  value={formData.website}
                />
              </Grid>
            </Grid>
          </Card>
          <Grid
            component='div'
            container
            style={{ justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap', marginTop: '16px' }}
          >
            <Button
              id='createCustomerProfileGoToCustomerProfilesBtn'
              type='button'
              variant='contained'
              size='medium'
              className='btn-default btn-action-basic btn-transparent-initial btn-cancel'
              onClick={() => {
                navigate(ROUTES.CUSTOMER_PROFILES);
              }}
            >
              {`${t('buttons.goBack')}`}
            </Button>
            <Button
              id='createCustomerProfileSubmitBtn'
              type='submit'
              variant='contained'
              disabled={pendingStatus}
              className='btn-default btn-action-basic'
            >
              {pendingStatus ? 'Submitting...' : 'Submit'}
            </Button>
          </Grid>
        </form>
      </div>
    </>
  );
};
